/// <amd-module name="Build/Common/Library/Components/UpgradeServiceConnectionHeader" />
import * as React from "react";

import { BuildDefinition, BuildRepository } from "Build/Client/Contracts/Build";
import { PipelineServiceConnection } from "Build/Client/Contracts/DataProvider";
import { IInstallAppService } from "Build/Common/Library/InstallAppService";
import { IPipelineService } from "Build/Common/Library/PipelineService";
import * as Resources from "Build/Common/Library/Resources";
import { ISourceProvider } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IButtonProps } from "VSSUI/Button";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export interface UpgradeServiceConnectionHeaderProps extends IVssComponentProperties {
    connection: PipelineServiceConnection;
    definition: BuildDefinition;
}

export interface ServiceConnectionRecommendation {
    commonConnectionId: string;
    userConnectionId: string;
    appInstalled: boolean;
    appInstalledForRepository: boolean;
    installAppUrlRoot: string;
    userCanInstall: boolean;
    repositoryMappedToAnotherCollection: boolean;
}

export interface UpgradeServiceConnectionHeaderState {
    connection: PipelineServiceConnection;    
    useAppConnectionDescription?: string;
    useAppConnectionAction?: IButtonProps;
    definition?: BuildDefinition;
}

export class UpgradeServiceConnectionHeader extends VssComponent<UpgradeServiceConnectionHeaderProps, UpgradeServiceConnectionHeaderState, IVssComponentContext> {

    constructor(props: UpgradeServiceConnectionHeaderProps, context: IVssComponentContext) {
          
        super(props, context);

        const state: UpgradeServiceConnectionHeaderState = {
            useAppConnectionAction: undefined,
            useAppConnectionDescription: Resources.PreferAppConnectionChange,
            connection: this.props.connection || {},
            definition: this.props.definition || {}
        };

        this.state = state;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._checkForAppConnectionUpgrade();
    }

    public render(): JSX.Element {
        if (!this.state.useAppConnectionAction) {
            return <></>;
        }

        return <div className= {css(
            "page-content",
            "flex-row",
            "flex-noshrink",
            this.props.className)}>
            <MessageCard
                className="flex-grow"
                severity={MessageCardSeverity.Info}
                onDismiss={this._closeConnectionCard}
                buttonProps={[this.state.useAppConnectionAction]}>
                <FormatComponent format={this.state.useAppConnectionDescription!}>
                    <FPSLink
                        href={this.state.connection.appHelpLink}>
                        {this.state.connection.appName}
                    </FPSLink>
                </FormatComponent>
            </MessageCard>
        </div>;
    }

    private _getRecommendedServiceConnection(definition: BuildDefinition, nonce: string): Promise<ServiceConnectionRecommendation> {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.app-serviceconnections-recommendation-data-provider";
        const parameters = {
            sourceProvider: this._getSourceProvider().getKey(),
            repositoryId: definition.repository.id,
            repositoryName: definition.repository.name,
            connectionId: this.state.connection.connectionId,
            strongBoxKey: "useWellKnownStrongBoxLocation",
            createOAuthConnectionIfNeeded: false,
            login: this.state.connection.login,
        };
        return contributionService.getDataAsync<ServiceConnectionRecommendation>(dataProviderId, parameters, true)
            .then((serviceConnectionRecommendation) => {
                if (!serviceConnectionRecommendation) {
                    const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                    throw new Error(exception && exception.message);
                }
                return serviceConnectionRecommendation;
            });
    }

    private _closeConnectionCard = () => {
        this.setState({ useAppConnectionAction: undefined });
    }

    private _switchToAppConnection = async (connectionId: string) => {
        const definition = this.state.definition!;
        definition.repository.properties = definition.repository.properties || [];
        definition.repository.properties["connectedServiceId"] = connectionId;

        this._closeConnectionCard();

        try {
            await this.pipelineService.savePipeline(definition);

            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            await contributionService.getDataAsync<boolean>("ms.vss-build-web.pipeline-edit-continuation-data-provider", {
                complete: true
            }, true);

            this.setState({definition}, this._closeConnectionCard);

            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent("Build", "UpgradeServiceConnectionHeader.AppConnectionUpgradeFromOAuth",  {
                GitHubApp: "AddAppConnection",
                GitHubAppRepo: definition.repository.id,
                action: "ChangeConnectionToGitHubApp",
            });
          
            // show a toast on complete if this was a user-driven action
            if (!this.state.connection.autoUpgradeToAppConnection) {
                this.globalMessageService.addToast({
                    message: Resources.ConnectionChanged,
                    duration: this.ToastDuration
                });
            }
        }
        catch (e) {
            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent("Build", "UpgradeServiceConnectionHeader.AppConnectionUpgradeFromOAuthError",  {
                message: e && e.message,
                e
            });

            this.globalMessageService.addToast({
                    message: Resources.SavePipelineErrorMessage,
                    duration: this.ToastDuration
                });  
        }
    }

    private _getSourceProvider(): ISourceProvider {
        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        return this.state.definition && sourceProviderService.getSourceProvider(this.state.definition.repository.type);
    }

    private async _checkForAppConnectionUpgrade() {
         if (!this.state.connection.isAppConnection && !!this.state.connection.appName) {
            try {
                const definition = this.state.definition!;
                const nonce = this.state.connection.nonce;
                const serviceConnectionRecommendation = await this._getRecommendedServiceConnection(definition, nonce);
                const recommendedConnectionId = serviceConnectionRecommendation.commonConnectionId;
                if (recommendedConnectionId) {
                    // there is an app connection available
                    if (this.state.connection.autoUpgradeToAppConnection) {
                        // automatically switch to start using it, the user has likely gone through 'installApp'
                        this._switchToAppConnection(recommendedConnectionId);
                    }
                    else {
                        // there is an existing app connection, so offer to change to that connection
                        const buttonProps: IButtonProps = {
                            text: Resources.ChangeToAppConnection,
                            onClick: () => this._switchToAppConnection(recommendedConnectionId)
                        };
                        this.setState({ useAppConnectionAction: buttonProps, useAppConnectionDescription: Resources.PreferAppConnectionChange });
                    }
                }
                else if (!serviceConnectionRecommendation.appInstalledForRepository && serviceConnectionRecommendation.userCanInstall) {
                    // the App is not installed, but the user is able to install it, so offer to install it
                    const buttonProps: IButtonProps = {
                        text: Resources.InstallApp,
                        onClick: () =>
                            this._installApp(
                                definition.id,
                                nonce,
                                serviceConnectionRecommendation.installAppUrlRoot,
                                definition.repository)
                    };
                    this.setState({ useAppConnectionAction: buttonProps, useAppConnectionDescription: Resources.PreferAppConnectionInstall });
                }
            }
            catch(error) {
                const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                telemetryService.publishEvent("Build", "UpgradeServiceConnectionHeader.CheckForAppConnectionUpgradeError",  {
                    message: error && error.message,
                    error
                });
            }
        }
    }

    private get pipelineService(): IPipelineService {
        return this.context.pageContext.getService<IPipelineService>("IPipelineService");
    }

    private get globalMessageService(): IGlobalMessagesService {
        return this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
    }

    private _installApp = async (pipelineId: number, nonce: string, installAppUrlRoot: string, repository: BuildRepository) => {
        const installAppService = this.context.pageContext.getService<IInstallAppService>("IInstallAppService");
        installAppService.installAppForEdit(pipelineId, nonce, installAppUrlRoot, repository);
    }
    readonly ToastDuration = 5000;
}

